import type { Moneda } from '@/shared/billing/currency';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AccessDenied, Button, ConfirmModal, ModalShell } from '@/presentation/components/common';
import { useAuth } from '@/presentation/contexts/AuthContext';
import { useToast } from '@/presentation/contexts/ToastContext';
import { httpClient } from '@/infrastructure/http/client/httpClient';
import { formatPeriodLabel } from '@/presentation/components/billing/ClinicInvoiceDocument';
import { TransferDataModal } from '@/presentation/components/billing/TransferDataModal';

/** Pago informado por una clínica desde Coofit con comprobante (PanelAvisoPago). */
type PaymentNotice = {
  id: number;
  tenantId: string;
  clinica: string;
  clinicaEstado: string;
  facturaId: number;
  periodo: string;
  monto: number;
  moneda: Moneda;
  saldoActual: number;
  facturaPagada: boolean;
  fechaPago: string;
  medioPagoLabel: string | null;
  comentario: string | null;
  archivoNombre: string;
  estado: 'pendiente' | 'aprobado' | 'rechazado' | 'cancelado';
  informadoPor: string | null;
  createdAt: string;
  revisadoPor: string | null;
  revisadoAt: string | null;
  motivoRechazo: string | null;
  suspendidaAlRechazar: boolean;
  zafoSuspension: boolean;
  /** Estaba suspendida y este aviso la reactivó: rechazarlo la vuelve a suspender sí o sí. */
  reactivoClinica: boolean;
};

const resuspendsOnReject = (n: PaymentNotice | null) => Boolean(n?.reactivoClinica && !n.facturaPagada);

type Filter = 'pendiente' | 'todos';

const money = (n: number, locale: string, moneda: Moneda = 'ARS') =>
  `${moneda === 'USD' ? 'US$' : '$'} ${n.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const ESTADO_BADGE: Record<PaymentNotice['estado'], string> = {
  pendiente: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  aprobado: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  rechazado: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200',
  cancelado: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
};

/** Avisa al menú que cambió la cantidad de pendientes (refresca el contador). */
const notifyNoticesChanged = () => window.dispatchEvent(new Event('panel:payment-notices-changed'));

export const PaymentNoticesPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { canOperateBilling } = useAuth();
  const { error: showError, success } = useToast();
  const [filter, setFilter] = useState<Filter>('pendiente');
  const [items, setItems] = useState<PaymentNotice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [toApprove, setToApprove] = useState<PaymentNotice | null>(null);
  const [toReject, setToReject] = useState<PaymentNotice | null>(null);
  const [motivo, setMotivo] = useState('');
  const [suspender, setSuspender] = useState(true);
  const [transferOpen, setTransferOpen] = useState(false);

  const locale = i18n.language;

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await httpClient.get<PaymentNotice[]>('/panel-pagos/avisos', { params: { estado: filter } });
      setItems(res.data ?? []);
    } catch {
      showError(t('pages.paymentNotices.loadError'));
    } finally {
      setIsLoading(false);
    }
  }, [filter, showError, t]);

  useEffect(() => {
    if (canOperateBilling) void load();
  }, [canOperateBilling, load]);

  const openFile = async (notice: PaymentNotice) => {
    // Se abre la pestaña antes del await para que el navegador no la bloquee como popup.
    // (sin 'noopener' en features: con eso window.open devuelve null; se corta el opener a mano).
    const win = window.open('', '_blank');
    if (win) win.opener = null;
    try {
      const res = await httpClient.get<{ url: string }>(`/panel-pagos/avisos/${notice.id}/archivo`);
      if (win) win.location.href = res.data.url;
      else window.open(res.data.url, '_blank', 'noopener');
    } catch {
      win?.close();
      showError(t('pages.paymentNotices.fileError'));
    }
  };

  const handleApprove = async () => {
    if (!toApprove) return;
    setBusyId(toApprove.id);
    try {
      const res = await httpClient.post<{ reactivated: boolean }>(`/panel-pagos/avisos/${toApprove.id}/aprobar`);
      success(
        res.data?.reactivated ? t('pages.paymentNotices.approvedReactivated') : t('pages.paymentNotices.approved')
      );
      setToApprove(null);
      notifyNoticesChanged();
      await load();
    } catch (err) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      showError(message || t('pages.paymentNotices.actionError'));
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async () => {
    if (!toReject) return;
    if (!motivo.trim()) {
      showError(t('pages.paymentNotices.reasonRequired'));
      return;
    }
    setBusyId(toReject.id);
    try {
      const res = await httpClient.post<{ suspended: boolean }>(`/panel-pagos/avisos/${toReject.id}/rechazar`, {
        motivo: motivo.trim(),
        suspender: toReject.zafoSuspension && suspender,
      });
      success(res.data?.suspended ? t('pages.paymentNotices.rejectedSuspended') : t('pages.paymentNotices.rejected'));
      setToReject(null);
      notifyNoticesChanged();
      await load();
    } catch (err) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      showError(message || t('pages.paymentNotices.actionError'));
    } finally {
      setBusyId(null);
    }
  };

  if (!canOperateBilling) return <AccessDenied />;

  return (
    <div className="panel-page">
      <div className="panel-page-header">
        <div className="min-w-0">
          <h1 className="panel-page-title">{t('pages.paymentNotices.title')}</h1>
          <p className="panel-page-subtitle hidden sm:block">{t('pages.paymentNotices.subtitle')}</p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value === 'todos' ? 'todos' : 'pendiente')}
            className="h-8 rounded-md border border-gray-300 bg-white px-2 text-sm text-slate-900 shadow-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            aria-label={t('pages.paymentNotices.filter')}
          >
            <option value="pendiente">{t('pages.paymentNotices.filterPending')}</option>
            <option value="todos">{t('pages.paymentNotices.filterAll')}</option>
          </select>
          <Button variant="secondary" size="sm" onClick={() => setTransferOpen(true)}>
            {t('pages.paymentNotices.transferData')}
          </Button>
          <Button variant="secondary" size="sm" onClick={() => void load()} disabled={isLoading}>
            {t('common.refresh')}
          </Button>
        </div>
      </div>

      <div className="panel-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500 dark:bg-gray-900/60 dark:text-slate-400">
              <tr>
                <th className="px-4 py-3">{t('pages.paymentNotices.colDate')}</th>
                <th className="px-4 py-3">{t('pages.paymentNotices.colClinic')}</th>
                <th className="px-4 py-3">{t('pages.paymentNotices.colPeriod')}</th>
                <th className="px-4 py-3 text-right">{t('pages.paymentNotices.colAmount')}</th>
                <th className="px-4 py-3">{t('pages.paymentNotices.colPayment')}</th>
                <th className="px-4 py-3">{t('pages.paymentNotices.colStatus')}</th>
                <th className="px-4 py-3 text-right">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-gray-700">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                    {t('common.loading')}
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                    {filter === 'pendiente' ? t('pages.paymentNotices.emptyPending') : t('pages.paymentNotices.empty')}
                  </td>
                </tr>
              ) : (
                items.map((n) => (
                  <tr key={n.id} className="align-top">
                    <td className="whitespace-nowrap px-4 py-3 text-slate-600 dark:text-slate-300">
                      {new Date(n.createdAt).toLocaleString(locale, { dateStyle: 'short', timeStyle: 'short' })}
                      {n.informadoPor ? <div className="text-[11px] text-slate-400">{n.informadoPor}</div> : null}
                    </td>
                    <td className="px-4 py-3 font-medium text-slate-900 dark:text-white">
                      {n.clinica}
                      {n.clinicaEstado !== 'active' ? (
                        <div className="text-[11px] font-normal text-slate-500">{n.clinicaEstado}</div>
                      ) : null}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">{formatPeriodLabel(n.periodo, locale)}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums">
                      {money(n.monto, locale, n.moneda)}
                      {n.estado === 'pendiente' && n.facturaPagada ? (
                        <div className="text-[11px] text-rose-600">{t('pages.paymentNotices.alreadyPaid')}</div>
                      ) : n.estado === 'pendiente' && Math.abs(n.saldoActual - n.monto) > 0.01 ? (
                        <div className="text-[11px] text-amber-600">
                          {t('pages.paymentNotices.currentBalance', { amount: money(n.saldoActual, locale, n.moneda) })}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                      <div className="whitespace-nowrap">
                        {new Date(`${n.fechaPago}T12:00:00`).toLocaleDateString(locale)}
                        {n.medioPagoLabel ? ` · ${n.medioPagoLabel}` : ''}
                      </div>
                      {n.comentario ? <div className="mt-0.5 max-w-xs text-xs text-slate-500">{n.comentario}</div> : null}
                      <button
                        type="button"
                        onClick={() => void openFile(n)}
                        className="mt-1 text-xs font-medium text-teal-700 hover:underline dark:text-teal-400"
                        title={n.archivoNombre}
                      >
                        {t('pages.paymentNotices.viewReceipt')}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${ESTADO_BADGE[n.estado]}`}>
                        {t(`pages.paymentNotices.status.${n.estado}`)}
                      </span>
                      {n.revisadoPor ? (
                        <div className="mt-1 text-[11px] text-slate-400">
                          {n.revisadoPor}
                          {n.revisadoAt ? ` · ${new Date(n.revisadoAt).toLocaleDateString(locale)}` : ''}
                        </div>
                      ) : null}
                      {n.motivoRechazo ? <div className="mt-1 max-w-xs text-xs text-rose-700">{n.motivoRechazo}</div> : null}
                      {n.suspendidaAlRechazar ? (
                        <div className="mt-1 text-[11px] text-rose-700">{t('pages.paymentNotices.suspendedOnReject')}</div>
                      ) : null}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      {n.estado === 'pendiente' ? (
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="secondary"
                            disabled={busyId !== null}
                            onClick={() => {
                              setMotivo('');
                              setSuspender(true);
                              setToReject(n);
                            }}
                          >
                            {t('pages.paymentNotices.reject')}
                          </Button>
                          <Button
                            size="sm"
                            disabled={busyId !== null || n.facturaPagada}
                            onClick={() => setToApprove(n)}
                          >
                            {t('pages.paymentNotices.approve')}
                          </Button>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ConfirmModal
        isOpen={toApprove !== null}
        onClose={() => (busyId !== null ? undefined : setToApprove(null))}
        onConfirm={handleApprove}
        title={t('pages.paymentNotices.approveTitle')}
        message={
          toApprove
            ? t('pages.paymentNotices.approveMessage', {
                clinic: toApprove.clinica,
                period: formatPeriodLabel(toApprove.periodo, locale),
                amount: money(toApprove.monto, locale, toApprove.moneda),
              })
            : ''
        }
        confirmText={t('pages.paymentNotices.approve')}
        isLoading={busyId !== null}
      />

      <ModalShell
        isOpen={toReject !== null}
        onClose={() => (busyId !== null ? undefined : setToReject(null))}
        title={t('pages.paymentNotices.rejectTitle')}
        subtitle={toReject ? `${toReject.clinica} · ${formatPeriodLabel(toReject.periodo, locale)}` : undefined}
        size="md"
        footer={
          <div className="flex w-full justify-end gap-2">
            <Button variant="secondary" onClick={() => setToReject(null)} disabled={busyId !== null}>
              {t('common.cancel')}
            </Button>
            <Button onClick={() => void handleReject()} isLoading={busyId !== null} disabled={!motivo.trim()}>
              {resuspendsOnReject(toReject) || (toReject?.zafoSuspension && suspender)
                ? t('pages.paymentNotices.rejectAndSuspend')
                : t('pages.paymentNotices.reject')}
            </Button>
          </div>
        }
      >
        <div className="space-y-4 p-1">
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('pages.paymentNotices.reason')}
            </span>
            <textarea
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              maxLength={1000}
              rows={3}
              placeholder={t('pages.paymentNotices.reasonPlaceholder')}
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
            <span className="mt-1 block text-xs text-slate-500">{t('pages.paymentNotices.reasonHint')}</span>
          </label>

          {resuspendsOnReject(toReject) ? (
            <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900 dark:border-rose-900/40 dark:bg-rose-900/20 dark:text-rose-100">
              <p className="font-semibold">{t('pages.paymentNotices.reactivatedTitle')}</p>
              <p className="mt-1">{t('pages.paymentNotices.reactivatedText')}</p>
            </div>
          ) : toReject?.zafoSuspension ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-100">
              <p className="font-semibold">{t('pages.paymentNotices.notSuspendedTitle')}</p>
              <p className="mt-1">{t('pages.paymentNotices.notSuspendedText')}</p>
              <label className="mt-2 flex items-center gap-2 font-medium">
                <input
                  type="checkbox"
                  checked={suspender}
                  onChange={(e) => setSuspender(e.target.checked)}
                  className="h-4 w-4 rounded border-amber-400 text-rose-600 focus:ring-rose-500"
                />
                {t('pages.paymentNotices.suspendNow')}
              </label>
            </div>
          ) : null}
        </div>
      </ModalShell>

      <TransferDataModal isOpen={transferOpen} onClose={() => setTransferOpen(false)} />
    </div>
  );
};
